import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Avatar, ButtonText, PayoutTotalsGrid } from 'components';
import { Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { IPayee, IPayoutOverviewTotalsDisplay } from 'types';
import { centsToDollarsDisplay } from 'utils';
import { Bottom, Header, HeaderError, HeaderSection, Layout, NoteInput, NoteWrapper, StyledText, UserInfo, UserInfoLayout } from './styles';

const EditHoldbackTrigger = styled(ButtonText)`
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  color: ${theme.color.primary[600]};
  text-decoration: none;
`;

const Main = styled.section`
  flex-basis: ${toRem(1200)};

  div {
    justify-items: center;

    @media (max-width: ${theme.breakpoint.xlarge}px) {
      justify-items: stretch;
    }
  }
`;

const JobTitle = styled(Text).attrs({
  size: 'small',
})`
  color: ${theme.color.neutral[600]};
`;

interface IPayeeReviewItemProps {
  className?: string;
  isLoading?: boolean;
  payee: IPayee;
  errorMessage?: string;
  onEditHoldback: () => void;
  onEditNote: (note: string) => void;
  includedPayouts: IPayoutOverviewTotalsDisplay;
}

export const PayeeReviewItem = ({ className, isLoading, payee, errorMessage, onEditHoldback, onEditNote, includedPayouts, ...rest }: IPayeeReviewItemProps) => {
  const { employee, gross, note } = payee;
  const userImage = employee.user?.avatarUrl;
  const hasPaymentMethod = !!employee.user?.card;

  return (
    <Layout className={className} data-cy="payee-review-item" {...rest}>
      {errorMessage && <HeaderError data-cy="header-error">{errorMessage}</HeaderError>}
      <Header $wrap>
        <HeaderSection className="user-info">
          <UserInfoLayout>
            <Avatar data-cy="payee-avatar" id={payee.id} username={payee.name} image={userImage} hasPaymentMethod={hasPaymentMethod} isLoading={isLoading} />
            <UserInfo>
              <StyledText data-cy="payee-name">{isLoading ? <Skeleton width={77} /> : payee.name}</StyledText>
              <JobTitle data-cy="payee-job-title">{isLoading ? <Skeleton width={77} /> : employee.job?.title}</JobTitle>
              <StyledText data-cy="payee-gross">{isLoading ? <Skeleton width={50} /> : centsToDollarsDisplay(gross)}</StyledText>
            </UserInfo>
          </UserInfoLayout>
        </HeaderSection>
        <Main>
          <PayoutTotalsGrid payee={payee} includedPayouts={includedPayouts} isLoading={isLoading} editHoldbackTrigger={<EditHoldbackTrigger data-cy="edit-holdback" label="Edit" onClick={onEditHoldback} />} />
        </Main>
      </Header>
      <Bottom>
        <NoteWrapper>
          <NoteInput type="text" data-cy="note-input" maxLength={100} placeholder="Enter an optional note" defaultValue={note} onBlur={(e: React.FocusEvent<HTMLInputElement>) => onEditNote(e.currentTarget.value)} />
        </NoteWrapper>
      </Bottom>
    </Layout>
  );
};
